/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Post-launch mobile bottom navigation: Features | center CTA | Pricing.
 */

import React from'react';
import { Box, Text, UnstyledButton } from'@/shared/ui';
import { useTranslation } from'react-i18next';
import { useNavigate } from'react-router';
import { useAuth } from'@context/authentication';
import { cn } from'@/shared/ui/utils/tailwind';
import { HousienBrandLogo } from'@/shared/ui/components/basic/logos';
import { BarChartIcon, RocketIcon } from'@/shared/ui/radix-icons';
import type { MobileNavProps } from'./MobileNav.types';

const indicator = (
 <Text component="span" className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-(--seed-base)"/>
);

const navButtonClass = (active: boolean) =>
  cn(
    'flex-1 flex flex-col items-center justify-center gap-(--space-1) relative',
    'text-(--text-tertiary) transition-colors duration-200',
    active &&'text-(--seed-base)',
  );

const labelClass ='text-meta tracking-(--tracking-wide) font-(--font-light) whitespace-nowrap leading-none';

export const PostLaunchMobileNav: React.FC<MobileNavProps> = ({ onScrollTo, activeSection }) => {
  const { t } = useTranslation('@pages/landing');
  const navigate = useNavigate();
  const auth = useAuth();

  return (
    <Box
      className={cn(
        'fixed bottom-0 left-0 right-0 z-1000',
        'h-(--bottom-nav-height,64px)',
        'bg-(--surface-primary)',
        'border-t border-(--border-subtle)',
        'pb-[env(safe-area-inset-bottom)]',
        'shadow-[var(--shadow-sm)]',
        'md:hidden',
      )}
    >
      <Box className="flex h-full items-stretch max-w-[600px] mx-auto relative">
        <UnstyledButton onClick={() => onScrollTo('features')} className={navButtonClass(activeSection ==='features')}>
          {activeSection ==='features' && indicator}
          <RocketIcon className="w-5 h-5" />
          <Text component="span" className={labelClass}>{t('navigation.features','Features')}</Text>
        </UnstyledButton>

        <div className="flex items-center justify-center px-(--space-2)">
          <UnstyledButton
            onClick={() => (auth.isAuthenticated ? navigate('/app/workspace') : auth.signin())}
            className={cn(
              'relative -mt-5 w-16 h-16 rounded-full',
              'bg-(--seed-base)',
              'flex items-center justify-center',
              'shadow-[var(--shadow-lg)]',
              'transition-transform duration-200 active:scale-95',
              'ring-[3px] ring-white',
            )}
            aria-label={
              auth.isAuthenticated
                ? t('postLaunch.hero.ctaDashboard','Go to Dashboard')
                : t('navigation.getStarted','Get Started')
            }
          >
            <HousienBrandLogo variant="icon" height={52} onDark />
          </UnstyledButton>
        </div>

        <UnstyledButton onClick={() => onScrollTo('pricing')} className={navButtonClass(activeSection ==='pricing')}>
          {activeSection ==='pricing' && indicator}
          <BarChartIcon className="w-5 h-5" />
          <Text component="span" className={labelClass}>{t('navigation.pricing','Pricing')}</Text>
        </UnstyledButton>
      </Box>
    </Box>
  );
};
